import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListPayStationsCashLevels } from 'src/features/payment/api/useListPayStationCashLevels';
import { payStationDeviceOptions, PayStationDeviceType } from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStationCashLevel } from 'src/features/payment/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { booleanBadgeOptions } from 'src/utils/constants';

const columnHelper = createColumnHelper<PayStationCashLevel>();

const initialSorting = {
    initialSortingState: [
        { id: 'device_type', desc: true },
        { id: 'value', desc: true },
    ],
};

export default function CashLevelsTable() {
    const { payStationId = '' } = useParams();
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting(initialSorting);
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters(
        payStationId ? { initialQueryFilters: { pay_station: payStationId } } : undefined,
    );
    const { parkingId } = useParking();

    const { data, isError, isLoading } = useListPayStationsCashLevels({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const columns = [
        columnHelper.accessor('device_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.row.original.device_type as PayStationDeviceType;
                if (typeof value !== 'number') return '-';
                return formatMessage(payStationDeviceOptions[value]);
            },
            meta: {
                title: formatMessage(paymentsMessages.deviceType),
                options: Object.entries(payStationDeviceOptions).map(([key, value]) => {
                    return { value: key, label: formatMessage(value) };
                }),
            },
        }),
        columnHelper.accessor('value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(paymentsMessages.nominalValue),
            },
        }),
        columnHelper.accessor('count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.count),
            },
        }),
        columnHelper.accessor('currency', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.currency),
            },
        }),
        columnHelper.accessor('in_recycle_box', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(paymentsMessages.inRecycleBox),
                badgeOptions: booleanBadgeOptions,
            },
        }),
    ];

    return (
        <DataTable
            columns={columns as any}
            data={data}
            pagination={pagination}
            sorting={sorting}
            filters={filters}
            search={search}
            semantics={{
                title: formatMessage(paymentsMessages.cashLevels),
                entryName: formatMessage(paymentsMessages.cashLevel).toLowerCase(),
                entryNames: formatMessage(paymentsMessages.cashLevels).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            enableSearchParams={false}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
}
